import throttle from 'lodash/throttle';

import {fetchJsonWithAbort, Headers, Request} from '../../helpers/fetch';
import {translate} from '../../helpers/i18n';

export const DEFAULT_RPC_HEADERS = {
	'content-type': 'application/json;charset=utf-8',
	accept: 'application/json;charset=utf-8',
};

const URL_PREFIX =
	window.location.hostname === 'ratsinfo.braunschweig.de'
		? 'https://www.braunschweig.de'
		: '';

const DEFAULT_SEARCH_PATH= '/suche.php';
const DEFAULT_SEARCH_ID = 'siteSearch';

export const DEFAULT_RPC_ENDPOINT_URL =
	URL_PREFIX + '/WEB-IES/sitekit-module/php/SP/SiteKit/Rpc/Server/Port.php';

export const DEFAULT_RPC_PARAMETERS = {
	action: 'SP\\SiteKit\\Rpc\\Search\\Service',
	method: 'suggest',
	type: 'rpc',
	tid: 4,
	version: '1.0',
};

export const DEFAULT_RPC_DATA = {
	core: null,
	facets: [],
	filter: [],
	lang: null,
	query: '',
	searchId: DEFAULT_SEARCH_ID,
	searchUrl: DEFAULT_SEARCH_PATH,
	sorting: [],
};

function escapeHtml(str) {
	return String(str).replace(/[&<>"']/g, (m) =>
		({'&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'}[m])
	);
}

const nf = new Intl.NumberFormat();

export function init(instance) {
	const state = {
		currentIndex: 0,
		numberOfResults: 0,
	};

	/* options */
	const rpcEndpointUrl = DEFAULT_RPC_ENDPOINT_URL;
	const rpcHeaders = DEFAULT_RPC_HEADERS;
	const rpcParameters = DEFAULT_RPC_PARAMETERS;
	const rpcData = DEFAULT_RPC_DATA;

	const searchPanelElement = document.getElementById(
		'bs-sidebar-search-panel'
	);
	/** @var {HTMLFormElement|null} formElement */
	const formElement = searchPanelElement.querySelector(
		'#bs-sidebar-search-form'
	);
	const inputElement = searchPanelElement.querySelector(
		'#bs-sidebar-search-input'
	);
	const searchOutputElement = searchPanelElement.querySelector(
		'#bs-sidebar-search-output'
	);

	const searchPath = (new URL(formElement.action ?? DEFAULT_SEARCH_PATH)).pathname;
	const searchId = formElement.elements['sp:search']?.value ?? DEFAULT_SEARCH_ID;
	const facetContentType = formElement.elements['sp:facets[contenttype]']?.value ?? 'article';

	const baseURL = new URL(URL_PREFIX || location.origin);
	baseURL.pathname = searchPath;
	baseURL.searchParams.set('action', 'submit');
	baseURL.searchParams.set('sp:search[]', searchId);
	baseURL.searchParams.set('sp:facets[contenttype]', facetContentType);

	rpcData.searchId = searchId;
	rpcData.searchUrl = searchPath;

	const searchLinksHtml = searchOutputElement.innerHTML;
	const searchLinksNumberOfItems = searchOutputElement.querySelectorAll(
		'.js-bs-sidebar-search-result-item'
	).length;

	function updateOutput(html) {
		searchOutputElement.innerHTML = html;
		instance.updateFocusableElements(searchPanelElement);
	}

	function renderSearchLinks() {
		state.numberOfResults = searchLinksNumberOfItems;
		return searchLinksHtml;
	}

	function renderEmptyResult() {
		return `
			<h3 class="bs-sidebar-headline">${translate('sidebar.Suggestions')}:</h3>
			<p class="bs-sidebar-text">${translate('sidebar.No hits')}</p>
		`;
	}

	function onSearchSuccess(data) {
		if (data.type === 'exception') {
			onSearchError(data.message);
			return;
		}

		/** @type {Array<{suggest: string, amount: number}>} listItems */
		const listItems = data.result.data.list;
		state.currentIndex = 0;
		state.numberOfResults = listItems.length;

		if (listItems.length) {
			let i = 0;

			updateOutput(`
				<h3 class="bs-sidebar-headline">${translate('sidebar.Suggestions')}:</h3>

				<ul class="bs-sidebar-list bs-sidebar-list--search">
					${listItems
						.slice(0, 5)
						.map(({suggest, amount}) => {
							suggest = (''  + suggest).trim();
							const url = new URL(baseURL);
							url.searchParams.set('sp:q[]', suggest);

							return `
								<li class="bs-sidebar-list-item">
									<a
										class="[ js-bs-sidebar-search-result-item ] bs-sidebar-list-item__link"
										data-js-bs-sidebar-search-result-index="${++i}"
										href="${escapeHtml(url)}"
									>
										<span class="bs-suggest-item">
											<span class="bs-suggest-item__label">${escapeHtml(suggest)}</span>
											<span class="bs-suggest-item__amount">${nf.format(amount)}</span>
										</span>
									</a>
								</li>
							`;
						})
						.join('')}
				</ul>
			`);
		} else {
			updateOutput(renderEmptyResult());
			inputElement.focus();
		}
	}

	function onSearchError(error) {
		state.currentIndex = 0;
		state.numberOfResults = 0;

		updateOutput(`
			<h3 class="bs-sidebar-headline">${escapeHtml(translate(
				'sidebar:Error occurred while searching'
			))}:</h3>
			<p class="bs-sidebar-text">${escapeHtml(error)}</p>
		`);
	}

	let currentSearch;

	function search() {
		if (currentSearch) {
			currentSearch.abort();
		}

		const query = inputElement.value;
		if (!query.trim()) {
			updateOutput(renderSearchLinks());
			return;
		}

		currentSearch = fetchJsonWithAbort(
			new Request(rpcEndpointUrl, {
				method: 'post',
				headers: new Headers(rpcHeaders),
				body: JSON.stringify({
					...rpcParameters,
					data: [
						{
							...rpcData,
							query: query,
						},
					],
				}),
			}),
			onSearchSuccess,
			onSearchError
		);
	}

	const onKeyUp = throttle(search, 150, {leading: false, trailing: true});
	const onSubmit = (e) => {
		e.stopPropagation();
	};

	inputElement.addEventListener('keyup', onKeyUp, {passive: true});
	formElement.addEventListener('submit', onSubmit);

	function moveSelection(direction) {
		const oldIndex = state.currentIndex || 0;
		state.currentIndex =
			(oldIndex + direction) % (state.numberOfResults + 1);
		applyCurrentSelection();
	}

	function applyCurrentSelection() {
		applySelection(findResultItemElementByIndex(state.currentIndex));
	}

	function applySelection(element) {
		// remove class from others
		[
			...searchPanelElement.querySelectorAll(
				'.bs-sidebar-list-item--selected'
			),
		].forEach((resultItemElement) =>
			resultItemElement.classList.remove('bs-sidebar-list-item--selected')
		);
		[
			...searchPanelElement.querySelectorAll(
				'.bs-sidebar-list-item__link--selected'
			),
		].forEach((resultItemElement) =>
			resultItemElement.classList.remove(
				'bs-sidebar-list-item__link--selected'
			)
		);

		if (element) {
			// add class to selected
			element.classList.add('bs-sidebar-list-item__link--selected');
			element.parentNode.classList.add('bs-sidebar-list-item--selected');
			element.focus();
		} else {
			inputElement.focus();
		}
	}

	function findResultItemElementByIndex(index) {
		return searchPanelElement.querySelector(
			`.js-bs-sidebar-search-result-item[data-js-bs-sidebar-search-result-index="${index}"]`
		);
	}

	function makeAction(resultItemElement) {
		resultItemElement.focus();
	}

	function handleActionKeyDown(e) {
		const {keyCode: keyCode = e.which, target} = e;

		// see https://github.com/moroshko/react-autosuggest/pull/388
		if (keyCode === 229) {
			return;
		}

		//e.preventDefault();
		e.stopPropagation();

		const isResultItem = target.matches(
			'.js-bs-sidebar-search-result-item'
		);
		if (isResultItem) {
			makeAction(target);
		}
	}

	function handleDirectionalKeyDown(e, direction) {
		e.preventDefault();
		e.stopPropagation();
		moveSelection(direction);
	}

	searchPanelElement.addEventListener('keydown', (e) => {
		const {key: key = e.char, keyCode: keyCode = e.which} = e;

		switch (keyCode) {
			// Arrow down
			case 40:
				handleDirectionalKeyDown(e, +1);
				break;

			// Arrow up
			case 38:
				handleDirectionalKeyDown(e, -1);
				break;

			// Enter/Space
			case 13:
			case 32:
				handleActionKeyDown(e);
				break;

			// Backspace/Arrow left/Arrow right
			//  -> Move focus to inputElement
			case 8:
			case 37:
			case 39:
				inputElement.focus();
				break;

			// Escape
			//  -> Clear input if not empty, otherwise close sidebar
			case 27:
				if (inputElement.value) {
					e.stopPropagation();
					inputElement.value = '';
					inputElement.focus();
				}
				break;

			default:
				// Looks like a "normal" character/number
				//  -> Move focus to inputElement
				if (!e.altKey && !e.ctrlKey && key.length === 1) {
					inputElement.focus();
				}
		}
	});

	searchPanelElement.addEventListener(
		'focusin',
		(e) => {
			if (e.target.matches('.js-bs-sidebar-search-result-item')) {
				state.currentIndex = parseInt(
					e.target.getAttribute(
						'data-js-bs-sidebar-search-result-index'
					),
					10
				);
			}

			applySelection(e.target);
		},
		{passive: true}
	);

	instance.searchPanel = {
		openPanel: () => {
			instance.openPanel(searchPanelElement, {animate: false});
			inputElement.focus();
		},
	};
}
